import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import './QuoteSpecIntake.css';

const API = process.env.REACT_APP_API_URL || 'https://meccaflex-backend-production.up.railway.app';

function formatUrgency(urgency, lang) {
  const map = {
    urgent:   lang === 'en' ? '🚀 Urgent'            : '🚀 Urgente',
    normal:   lang === 'en' ? '📅 Standard timeline' : '📅 Tempistiche standard',
    planning: lang === 'en' ? '🗓 Planning phase'    : '🗓 Fase di pianificazione',
  };
  return map[urgency] || urgency;
}

export default function QuoteSpecIntake() {
  const { leadId } = useParams();

  const [lead, setLead]         = useState(null);
  const [status, setStatus]     = useState('loading'); // loading | ready | submitting | success | error | not-found

  // form state
  const [dimensions,   setDimensions]   = useState('');
  const [quantity,     setQuantity]     = useState('1');
  const [requirements, setRequirements] = useState('');
  const [files,        setFiles]        = useState([]);
  const [dragOver,     setDragOver]     = useState(false);
  const [submitError,  setSubmitError]  = useState('');

  const lang = lead?.language || 'it';
  const t = (it, en) => lang === 'en' ? en : it;

  // ── Load lead summary ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!leadId) { setStatus('not-found'); return; }
    fetch(`${API}/api/contact/specs/${leadId}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) { setStatus('not-found'); return; }
        setLead(data.lead);
        setStatus(data.lead.specsAlreadySubmitted ? 'already-submitted' : 'ready');
      })
      .catch(() => setStatus('not-found'));
  }, [leadId]);

  // ── File handling ──────────────────────────────────────────────────────────
  const addFiles = useCallback((incoming) => {
    const allowed = /\.(stl|obj|step|stp|3mf|iges|igs|pdf|jpg|jpeg|png|webp|gif|zip)$/i;
    const valid = Array.from(incoming).filter(f => allowed.test(f.name));
    setFiles(prev => {
      const combined = [...prev, ...valid];
      return combined.slice(0, 5); // max 5
    });
  }, []);

  const removeFile = (idx) => setFiles(prev => prev.filter((_, i) => i !== idx));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setStatus('submitting');

    const fd = new FormData();
    fd.append('dimensions',   dimensions.trim());
    fd.append('quantity',     quantity || '1');
    fd.append('requirements', requirements.trim());
    files.forEach(f => fd.append('files', f));

    try {
      const res  = await fetch(`${API}/api/contact/specs/${leadId}`, { method: 'POST', body: fd });
      const data = await res.json();
      if (data.success) {
        setStatus('success');
      } else {
        setSubmitError(data.message || t('Errore durante l\'invio. Riprova.', 'Error during submission. Please try again.'));
        setStatus('ready');
      }
    } catch {
      setSubmitError(t('Errore di connessione. Riprova più tardi.', 'Connection error. Please try again later.'));
      setStatus('ready');
    }
  };

  // ── Renders ────────────────────────────────────────────────────────────────
  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div className="qsi-loading">
          <div className="qsi-spinner" />
          <span className="qsi-loading-text">{t('Caricamento...', 'Loading...')}</span>
        </div>
      );
    }

    if (status === 'not-found') {
      return (
        <div className="qsi-not-found">
          <div className="qsi-not-found-icon">🔍</div>
          <h2>{t('Link non valido', 'Invalid link')}</h2>
          <p>{t('Questo link non è valido o è scaduto. Contattaci direttamente a info@meccaflex.com', 'This link is invalid or expired. Contact us directly at info@meccaflex.com')}</p>
        </div>
      );
    }

    if (status === 'already-submitted') {
      return (
        <div className="qsi-already">
          <div className="qsi-already-icon">✅</div>
          <h2>{t('Specifiche già ricevute', 'Specifications already received')}</h2>
          <p>{t('Hai già inviato le specifiche per questo progetto. Il nostro team sta preparando il tuo preventivo.', 'You have already submitted specifications for this project. Our team is preparing your quote.')}</p>
        </div>
      );
    }

    if (status === 'success') {
      return (
        <div className="qsi-success">
          <div className="qsi-success-icon">✅</div>
          <h2>{t('Specifiche inviate!', 'Specifications sent!')}</h2>
          <p>{t('Grazie! Abbiamo ricevuto tutte le informazioni necessarie.', 'Thank you! We have received all the necessary information.')}</p>

          <div className="qsi-success-timeline">
            <div className="qsi-timeline-item">
              <div className="qsi-timeline-dot" />
              <span>{t('Il nostro team analizzerà le tue specifiche', 'Our team will review your specifications')}</span>
            </div>
            <div className="qsi-timeline-item">
              <div className="qsi-timeline-dot" />
              <span>{t('Riceverai un preventivo dettagliato entro 2 ore', 'You will receive a detailed quote within 2 hours')}</span>
            </div>
            <div className="qsi-timeline-item">
              <div className="qsi-timeline-dot" />
              <span>{t('Potremmo contattarti per ulteriori dettagli se necessario', 'We may contact you for further details if needed')}</span>
            </div>
          </div>
        </div>
      );
    }

    // ready | submitting
    const isSubmitting = status === 'submitting';

    return (
      <>
        {/* Original request recap */}
        <div className="qsi-intro">
          <h1 className="qsi-greeting">
            {t('Ciao', 'Hello')} <span>{lead?.name?.split(' ')[0]}</span>! 👋
          </h1>
          <p className="qsi-intro-text">
            {t(
              'Abbiamo ricevuto la tua richiesta e siamo pronti ad aiutarti. Per prepararti un preventivo preciso, abbiamo bisogno di qualche dettaglio in più sul tuo progetto.',
              'We received your enquiry and are ready to help. To prepare an accurate quote, we need a few more details about your project.',
            )}
          </p>
          <div className="qsi-original-request">
            <div className="qsi-original-label">{t('La tua richiesta originale', 'Your original request')}</div>
            <p className="qsi-original-text">{lead?.message}</p>
          </div>
          {lead?.urgency && (
            <div style={{ marginTop: '0.75rem', fontSize: '0.8rem', color: '#71717a' }}>
              {t('Urgenza', 'Urgency')}: {formatUrgency(lead.urgency, lang)}
            </div>
          )}
        </div>

        {/* Spec form */}
        <form className="qsi-form-card" onSubmit={handleSubmit}>
          <div className="qsi-form-title">
            {t('Dettagli del progetto', 'Project details')}
          </div>

          {submitError && <div className="qsi-error">{submitError}</div>}

          <div className="qsi-row">
            <div className="qsi-field">
              <label className="qsi-label" htmlFor="qsi-dimensions">
                {t('Dimensioni', 'Dimensions')}
                <span className="qsi-optional">{t('(facoltativo)', '(optional)')}</span>
              </label>
              <input
                id="qsi-dimensions"
                className="qsi-input"
                type="text"
                placeholder={t('es. 80×40×20 mm', 'e.g. 80×40×20 mm')}
                value={dimensions}
                onChange={e => setDimensions(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="qsi-field">
              <label className="qsi-label" htmlFor="qsi-quantity">
                {t('Quantità', 'Quantity')}
              </label>
              <input
                id="qsi-quantity"
                className="qsi-input"
                type="number"
                min="1"
                max="9999"
                placeholder="1"
                value={quantity}
                onChange={e => setQuantity(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          <div className="qsi-field">
            <label className="qsi-label" htmlFor="qsi-requirements">
              {t('Requisiti specifici', 'Specific requirements')}
              <span className="qsi-optional">{t('(facoltativo)', '(optional)')}</span>
            </label>
            <textarea
              id="qsi-requirements"
              className="qsi-textarea"
              rows={4}
              placeholder={t(
                'Es. materiale preferito, tolleranze, finitura superficiale, colore, uso previsto...',
                'E.g. preferred material, tolerances, surface finish, colour, intended use...',
              )}
              value={requirements}
              onChange={e => setRequirements(e.target.value)}
              disabled={isSubmitting}
            />
          </div>

          {/* File upload */}
          <div className="qsi-field">
            <label className="qsi-label">
              {t('File allegati', 'Attachments')}
              <span className="qsi-optional">{t('(max 5 file, 20 MB ciascuno)', '(max 5 files, 20 MB each)')}</span>
            </label>
            <div
              className={`qsi-upload-zone${dragOver ? ' drag-over' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
            >
              <input
                className="qsi-file-input"
                type="file"
                multiple
                accept=".stl,.obj,.step,.stp,.3mf,.iges,.igs,.pdf,.jpg,.jpeg,.png,.webp,.gif,.zip"
                onChange={e => addFiles(e.target.files)}
                disabled={isSubmitting}
              />
              <div className="qsi-upload-icon">📎</div>
              <div className="qsi-upload-text">
                <strong>{t('Clicca o trascina', 'Click or drag')}</strong>{' '}
                {t('i tuoi file qui', 'your files here')}
              </div>
              <div className="qsi-upload-types">
                STL · OBJ · STEP · 3MF · PDF · JPG · PNG · ZIP
              </div>
            </div>

            {files.length > 0 && (
              <div className="qsi-files-list">
                {files.map((f, i) => (
                  <div key={i} className="qsi-file-item">
                    <span className="qsi-file-item-icon">📄</span>
                    <span>{f.name}</span>
                    <span style={{ color: '#52525b', fontSize: '0.75rem', marginLeft: '0.25rem' }}>
                      ({(f.size / 1024 / 1024).toFixed(1)} MB)
                    </span>
                    <button
                      type="button"
                      className="qsi-file-remove"
                      onClick={() => removeFile(i)}
                      disabled={isSubmitting}
                      aria-label="Remove file"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="qsi-hint">
              {t(
                'File 3D (STL, STEP, OBJ) o disegni tecnici (PDF) aiutano a fornire un preventivo più accurato',
                '3D files (STL, STEP, OBJ) or technical drawings (PDF) help us provide a more accurate quote',
              )}
            </div>
          </div>

          <button
            type="submit"
            className="qsi-submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? t('Invio in corso...', 'Sending...')
              : t('Invia specifiche →', 'Send specifications →')}
          </button>
        </form>
      </>
    );
  };

  return (
    <div className="qsi-root">
      {/* Header */}
      <header className="qsi-header">
        <div className="qsi-logo-mark">M</div>
        <span className="qsi-brand">Mecca<span>Flex</span></span>
      </header>

      {/* Step indicator */}
      <main className="qsi-main">
        {(status === 'ready' || status === 'submitting') && (
          <div className="qsi-step">
            <div className="qsi-step-dot" />
            {t('Passaggio 2 di 3 — Specifiche progetto', 'Step 2 of 3 — Project specifications')}
          </div>
        )}

        {renderContent()}
      </main>

      {/* Footer */}
      <footer className="qsi-footer">
        © {new Date().getFullYear()} MeccaFlex SRLS · Campania, Italia ·{' '}
        <a href="mailto:info@meccaflex.com" style={{ color: '#06b6d4', textDecoration: 'none' }}>
          info@meccaflex.com
        </a>
      </footer>
    </div>
  );
}
